import React, { useState } from 'react';
import { Download, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '../ui/dialog';
import { toast } from 'sonner';

const BRAND = 'Cravin Crrispy';

const formatCustomizations = (opts = []) => {
  if (!Array.isArray(opts) || opts.length === 0) return '-';
  return opts
    .map((o) => {
      const choices = (o.choices || [])
        .map((c) => `${c.name}${Number(c.price) ? ` (+£${Number(c.price).toFixed(2)})` : ''}`)
        .join(', ');
      return `${o.name}${o.required ? ' *' : ''}: ${choices || '-'}`;
    })
    .join('\n');
};

const buildBasicRows = (items) =>
  items.map((it) => [
    it.name || '',
    it.category || '-',
    `£${Number(it.price || 0).toFixed(2)}`,
  ]);

const buildFullRows = (items) =>
  items.map((it) => [
    it.name || '',
    it.category || '-',
    it.subcategory || '-',
    `£${Number(it.price || 0).toFixed(2)}`,
    it.available ? 'Yes' : 'No',
    it.description || '-',
    formatCustomizations(it.customization_options),
  ]);

export const ExportMenuDialog = ({
  menuItems = [],
  filteredMenuItems = [],
  filterActive = false,
  categoryFilterLabel = 'All',
}) => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState('basic');
  const [scope, setScope] = useState(filterActive ? 'filtered' : 'all');
  const [busy, setBusy] = useState(false);

  const handleExport = () => {
    const items = scope === 'filtered' ? filteredMenuItems : menuItems;
    if (!items || items.length === 0) {
      toast.error('No items to export');
      return;
    }

    setBusy(true);
    try {
      // Landscape gives more room for "Full" export tables
      const doc = new jsPDF({
        orientation: preset === 'full' ? 'landscape' : 'portrait',
        unit: 'pt',
        format: 'a4',
      });
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(`${BRAND} — Menu Export`, 40, 46);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100);
      const meta = [
        `Generated: ${new Date().toLocaleString('en-GB')}`,
        `Scope: ${scope === 'filtered' ? `Filtered (${categoryFilterLabel})` : 'All items'}`,
        `Items: ${items.length}`,
        `Preset: ${preset === 'basic' ? 'Basic' : 'Full'}`,
      ].join('   |   ');
      doc.text(meta, 40, 62);
      doc.setTextColor(0);

      const head =
        preset === 'basic'
          ? [['Name', 'Category', 'Price']]
          : [['Name', 'Category', 'Subcategory', 'Price', 'Available', 'Description', 'Customisations']];

      const body = preset === 'basic' ? buildBasicRows(items) : buildFullRows(items);

      autoTable(doc, {
        head,
        body,
        startY: 78,
        margin: { left: 40, right: 40 },
        styles: {
          font: 'helvetica',
          fontSize: preset === 'full' ? 8 : 10,
          cellPadding: 5,
          overflow: 'linebreak',
          valign: 'top',
        },
        headStyles: {
          fillColor: [220, 38, 38], // brand red
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        columnStyles:
          preset === 'basic'
            ? {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 140 },
                2: { cellWidth: 70, halign: 'right' },
              }
            : {
                0: { cellWidth: 110 },
                1: { cellWidth: 90 },
                2: { cellWidth: 90 },
                3: { cellWidth: 55, halign: 'right' },
                4: { cellWidth: 60, halign: 'center' },
                5: { cellWidth: 180 },
                6: { cellWidth: 'auto' },
              },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageNum = data.pageNumber;
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text(
            `Page ${pageNum} of ${pageCount}`,
            pageWidth - 40,
            doc.internal.pageSize.getHeight() - 20,
            { align: 'right' }
          );
          doc.setTextColor(0);
        },
      });

      const dateStr = new Date().toISOString().slice(0, 10);
      const fname = `cravin-crrispy-menu-${preset}-${dateStr}.pdf`;
      doc.save(fname);
      toast.success(`Exported ${items.length} item${items.length === 1 ? '' : 's'} to PDF`);
      setOpen(false);
    } catch (err) {
      console.error('PDF export failed', err);
      toast.error('Failed to generate PDF');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="rounded-full gap-2"
          data-testid="export-menu-btn"
        >
          <Download className="h-4 w-4" />
          Export Menu
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="export-menu-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-red-600" />
            Export Menu to PDF
          </DialogTitle>
          <DialogDescription>
            Choose what to include and which items to export.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Fields preset */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Fields to include</Label>
            <RadioGroup
              value={preset}
              onValueChange={setPreset}
              className="space-y-2"
              data-testid="export-preset-group"
            >
              <label
                htmlFor="preset-basic"
                className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50"
              >
                <RadioGroupItem value="basic" id="preset-basic" data-testid="export-preset-basic" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Basic</div>
                  <div className="text-xs text-muted-foreground">
                    Name, Category, Price
                  </div>
                </div>
              </label>
              <label
                htmlFor="preset-full"
                className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50"
              >
                <RadioGroupItem value="full" id="preset-full" data-testid="export-preset-full" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Full</div>
                  <div className="text-xs text-muted-foreground">
                    Name, Category, Subcategory, Price, Availability, Description, Customisations
                  </div>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Scope */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Which items?</Label>
            <RadioGroup
              value={scope}
              onValueChange={setScope}
              className="space-y-2"
              data-testid="export-scope-group"
            >
              <label
                htmlFor="scope-all"
                className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50"
              >
                <RadioGroupItem value="all" id="scope-all" data-testid="export-scope-all" />
                <div className="flex-1">
                  <div className="text-sm font-medium">All items</div>
                  <div className="text-xs text-muted-foreground">
                    {menuItems.length} item{menuItems.length === 1 ? '' : 's'} across all categories
                  </div>
                </div>
              </label>
              <label
                htmlFor="scope-filtered"
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 ${
                  !filterActive ? 'opacity-60' : ''
                }`}
              >
                <RadioGroupItem
                  value="filtered"
                  id="scope-filtered"
                  disabled={!filterActive}
                  data-testid="export-scope-filtered"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">Current filter only</div>
                  <div className="text-xs text-muted-foreground">
                    {filterActive
                      ? `${filteredMenuItems.length} item${filteredMenuItems.length === 1 ? '' : 's'} — ${categoryFilterLabel}`
                      : 'No filters active — apply a search or category to enable'}
                  </div>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            data-testid="export-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white gap-2"
            data-testid="export-confirm-btn"
          >
            <Download className="h-4 w-4" />
            {busy ? 'Generating…' : 'Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
